import React, { useEffect, useState } from 'react'
import injectSheet from 'react-jss'
import { apiClient } from 'pobble-components/dist/api'
import { useHistory } from 'react-router-dom'
import Button from 'pobble-components/dist/button'
import ButtonElement from 'pobble-components/dist/button-element'
import { Modal, ModalContent, ModalFooter } from 'pobble-components/dist/modal'
import FormFile from 'pobble-components/dist/form-elements/form-file'
import { Image } from 'pobble-components/dist/image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCamera } from '@fortawesome/pro-regular-svg-icons/faCamera'
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft'
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons/faAngleRight'
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons/faTrashAlt'
import { toast } from 'pobble-components/dist/toast'
import { errorHandler } from 'pobble-components/dist/error-handler'
import { PARENT_PRIMARY_CTA } from './parent-theme'
import finished from './illustrations/parent-child.png'
import posthog from 'posthog-js'

const PARENT_WRITING_WORK_INCLUDE =
  'parent_writing.last_parent_writing_work_version,parent_writing.last_parent_writing_work_version.details,parent_writing.last_parent_writing_work_version.work,parent_writing.last_parent_writing_work_version.work.media,parent_writing.last_parent_writing_work_version.work.medium.image,parent_writing.last_parent_writing_work_version.work.medium.details'

const WORK_VERSION_RESPONSE_INCLUDE =
  'parent_writing_work_version.work,parent_writing_work_version.work.media,parent_writing_work_version.work.medium.image,parent_writing_work_version.work.medium.details'

const getWorkVersionFromResponse = data => {
  if (!data) return null
  return data.parent_writing_work_version || data
}

const getMediaFromWorkVersion = workVersion => {
  const media =
    (workVersion && workVersion.work && workVersion.work.media) || []
  return [...media].sort((a, b) => (a.order || 0) - (b.order || 0))
}

const getMediaImageUrl = media =>
  (media && (media.image_url || media.url || media.thumb_url)) || null

const applyWorkVersion = (
  workVersion,
  setWorkVersionId,
  setMediaItems,
  setActiveMediaIndex
) => {
  if (!workVersion || !workVersion.id) return

  const media = getMediaFromWorkVersion(workVersion)
  setWorkVersionId(workVersion.id)
  setMediaItems(media)
  setActiveMediaIndex(currentIndex => {
    if (!media.length) return 0
    if (currentIndex >= media.length) return media.length - 1
    return currentIndex
  })
}

export const ParentWritingDoneWizard = injectSheet({
  content: {
    width: '100%',
    maxWidth: '460px',
    textAlign: 'center',
    '& h3': {
      margin: '0 0 10px',
      color: '#111827',
      font: 'var(--heading--)'
    },
    '& p': {
      color: '#4b5563',
      font: 'var(--body)',
      lineHeight: 1.5
    },
    '& img': {
      display: 'block',
      margin: '0 auto'
    }
  },
  preview: {
    margin: '0 auto 12px',
    maxWidth: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    background: '#f9fafb'
  },
  previewImage: {
    width: '100%',
    display: 'block',
    maxHeight: '280px',
    objectFit: 'contain'
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '12px'
  },
  paginationButton: {
    width: '40px',
    height: '40px',
    borderRadius: '100%',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: 'var(--purple)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:disabled': {
      opacity: 0.4,
      cursor: 'not-allowed'
    },
    '&:hover:not(:disabled)': {
      background: '#faf5ff',
      borderColor: 'var(--purple--)'
    }
  },
  paginationLabel: {
    margin: '0',
    minWidth: '72px',
    color: '#4b5563',
    font: 'var(--body--)',
    fontWeight: 'bold'
  },
  deleteButton: {
    margin: '0 auto 16px',
    color: '#b91c1c !important',
    font: 'var(--body--)',
    fontWeight: 'bold',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    '&:disabled': {
      opacity: 0.5
    }
  },
  formFileContainer: {
    width: '100%'
  },
  uploadArea: {
    textAlign: 'left',
    marginBottom: '8px'
  },
  uploadBox: {
    background: '#faf5ff',
    border: '2px dashed var(--purple--)',
    borderRadius: '12px',
    position: 'relative',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'var(--purple)'
    }
  },
  uploadIcon: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    color: 'var(--purple)',
    font: 'var(--body--)',
    fontWeight: 'bold',
    padding: '40px 0',
    width: '100%',
    '& svg': {
      fontSize: '28px'
    }
  },
  stepHint: {
    margin: '0',
    color: '#6b7280',
    font: 'var(--body--)'
  },
  modalPrimary: PARENT_PRIMARY_CTA,
  modalSecondary: {
    color: '#666 !important',
    '&:hover': {
      color: '#666'
    }
  }
})(({ classes, isOpen, requestClose, parentWritingId }) => {
  const history = useHistory()
  const [step, setStep] = useState('upload')
  const [fetchPending, setFetchPending] = useState(false)
  const [uploadPending, setUploadPending] = useState(false)
  const [deletePending, setDeletePending] = useState(false)
  const [markDonePending, setMarkDonePending] = useState(false)
  const [workVersionId, setWorkVersionId] = useState(null)
  const [mediaItems, setMediaItems] = useState([])
  const [activeMediaIndex, setActiveMediaIndex] = useState(0)

  const busy = fetchPending || uploadPending || deletePending || markDonePending
  const activeMedia = mediaItems[activeMediaIndex]
  const activeImageUrl = getMediaImageUrl(activeMedia)
  const hasMedia = mediaItems.length > 0

  const resetWizard = () => {
    setStep('upload')
    setWorkVersionId(null)
    setMediaItems([])
    setActiveMediaIndex(0)
  }

  const loadParentWriting = () => {
    if (!parentWritingId) return Promise.resolve()

    setFetchPending(true)

    return apiClient
      .get(`/v3/parent_writings/${parentWritingId}`, {
        params: {
          include: PARENT_WRITING_WORK_INCLUDE
        }
      })
      .then(res => {
        const parentWriting = res.data.parent_writing
        const workVersion =
          parentWriting && parentWriting.last_parent_writing_work_version

        if (!workVersion || !workVersion.id) {
          toast.error(
            'We could not prepare your writing upload. Please try again from your saved list.'
          )
          requestClose()
          return
        }

        applyWorkVersion(
          workVersion,
          setWorkVersionId,
          setMediaItems,
          setActiveMediaIndex
        )
      })
      .catch(errorHandler)
      .finally(() => {
        setFetchPending(false)
      })
  }

  useEffect(
    () => {
      if (!isOpen || !parentWritingId) {
        return
      }

      setStep('upload')
      loadParentWriting()
    },
    [isOpen, parentWritingId] // eslint-disable-line
  )

  const handleClose = () => {
    if (busy) return
    resetWizard()
    requestClose()
  }

  const uploadPhoto = files => {
    if (!files || !files.length || !workVersionId) return

    const file = files[0]

    if (!/^image\/(jpeg|png)$/i.test(file.type)) {
      toast.error('Please choose a JPEG or PNG photo')
      return
    }

    const formData = new FormData()
    formData.append('image', file)
    formData.append('include', WORK_VERSION_RESPONSE_INCLUDE)

    setUploadPending(true)

    apiClient
      .post(
        `/v3/parent_writing_work_versions/${workVersionId}/create_medium`,
        formData
      )
      .then(res => {
        const workVersion = getWorkVersionFromResponse(res.data)
        applyWorkVersion(
          workVersion,
          setWorkVersionId,
          setMediaItems,
          setActiveMediaIndex
        )
        setActiveMediaIndex(getMediaFromWorkVersion(workVersion).length - 1)
        toast.success(hasMedia ? 'Photo added' : 'Photo uploaded')
      })
      .catch(errorHandler)
      .finally(() => {
        setUploadPending(false)
      })
  }

  const deletePhoto = () => {
    if (!workVersionId || !activeMedia || !activeMedia.id) return

    setDeletePending(true)

    apiClient
      .delete(`/v3/media/${activeMedia.id}`)
      .then(() => {
        const nextMedia = mediaItems.filter(item => item.id !== activeMedia.id)
        setMediaItems(nextMedia)
        setActiveMediaIndex(currentIndex => {
          if (!nextMedia.length) return 0
          if (currentIndex >= nextMedia.length) return nextMedia.length - 1
          return currentIndex
        })
        toast.success('Photo removed')
      })
      .catch(errorHandler)
      .finally(() => {
        setDeletePending(false)
      })
  }

  const markWritingDone = () => {
    if (!parentWritingId) return

    setMarkDonePending(true)

    posthog.capture('submit parent writing', {
      writing_type: 'uploaded'
    })

    apiClient
      .put(`/v3/parent_writings/${parentWritingId}/mark_done`)
      .then(() => {
        history.push('/parent/saved')
      })
      .catch(errorHandler)
      .finally(() => {
        setMarkDonePending(false)
      })
  }

  return (
    <Modal isOpen={isOpen} requestClose={handleClose} closeButton>
      {step === 'upload' && (
        <>
          <ModalContent className={classes.content}>
            <h3>Add a photo of your writing</h3>
            <p>
              Take clear photos of the writing you have done on paper. You can
              add more than one page and remove any photo you do not need.
            </p>
            {fetchPending && <p className={classes.stepHint}>Loading…</p>}
            {!fetchPending &&
              hasMedia && (
                <>
                  <div className={classes.preview}>
                    <Image
                      className={classes.previewImage}
                      src={activeImageUrl}
                      alt={`Uploaded writing page ${activeMediaIndex + 1}`}
                    />
                  </div>
                  {mediaItems.length > 1 && (
                    <div className={classes.pagination}>
                      <ButtonElement
                        className={classes.paginationButton}
                        disabled={busy || activeMediaIndex === 0}
                        onClick={() =>
                          setActiveMediaIndex(index => Math.max(0, index - 1))
                        }>
                        <FontAwesomeIcon icon={faAngleLeft} />
                      </ButtonElement>
                      <p className={classes.paginationLabel}>
                        {activeMediaIndex + 1} of {mediaItems.length}
                      </p>
                      <ButtonElement
                        className={classes.paginationButton}
                        disabled={
                          busy || activeMediaIndex >= mediaItems.length - 1
                        }
                        onClick={() =>
                          setActiveMediaIndex(index =>
                            Math.min(mediaItems.length - 1, index + 1)
                          )
                        }>
                        <FontAwesomeIcon icon={faAngleRight} />
                      </ButtonElement>
                    </div>
                  )}
                  <ButtonElement
                    className={classes.deleteButton}
                    disabled={busy}
                    onClick={deletePhoto}>
                    <FontAwesomeIcon icon={faTrashAlt} />
                    Remove this photo
                  </ButtonElement>
                </>
              )}
            {!fetchPending &&
              workVersionId && (
                <div className={classes.uploadArea}>
                  {/*<FormLabel small>
                    {hasMedia ? 'Add another photo' : 'Upload photo'}
                  </FormLabel>*/}
                  <FormFile
                    className={classes.formFileContainer}
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    onChange={uploadPhoto}>
                    <div className={classes.uploadBox}>
                      <span className={classes.uploadIcon}>
                        <FontAwesomeIcon icon={faCamera} />
                        {hasMedia ? 'Add another photo' : 'Choose a photo'}
                      </span>
                    </div>
                  </FormFile>
                </div>
              )}
          </ModalContent>
          <ModalFooter alignRight childrenGap>
            <Button
              simple
              tall
              className={classes.modalSecondary}
              action={handleClose}
              disabled={busy}>
              Cancel
            </Button>
            <Button
              minw
              tall
              className={classes.modalPrimary}
              disabled={busy || !hasMedia}
              action={() => setStep('confirm')}>
              Continue
            </Button>
          </ModalFooter>
        </>
      )}
      {step === 'confirm' && (
        <>
          <ModalContent className={classes.content}>
            <h3>Finished for now?</h3>
            <img width="320" height="320" src={finished} alt="Pobble Home" />
            <p>Nice work! When you're ready, mark it as done.</p>
          </ModalContent>
          <ModalFooter alignRight childrenGap>
            <Button
              simple
              tall
              className={classes.modalSecondary}
              action={() => setStep('upload')}
              disabled={busy}>
              Back
            </Button>
            <Button
              minw
              tall
              className={classes.modalPrimary}
              pending={markDonePending}
              action={markWritingDone}
              disabled={busy}>
              Save as done
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  )
})
